import React, { useState } from 'react';
import { useWeb3 } from '../context/Web3Context';
import { addBSCNetwork, switchToBSCNetwork, addTokenToWallet, isConnectedToBSC, getWalletProvider, PHONE_TOKEN_BSCSCAN } from '../utils/walletUtils';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'react-toastify';
import './AddToWallet.css';

const AddToWallet = () => {
  const { chainId, isConnected } = useWeb3();
  const { t } = useLanguage();
  const [isAddingNetwork, setIsAddingNetwork] = useState(false);
  const [isAddingToken, setIsAddingToken] = useState(false);

  const handleAddNetwork = async () => {
    if (!isConnected) {
      toast.error(t('pleaseConnectWallet') || 'Please connect your wallet first');
      return;
    }

    setIsAddingNetwork(true);
    try {
      const provider = getWalletProvider();
      if (!provider) {
        throw new Error(t('noWalletFound') || 'No wallet provider found');
      }

      // Check if already on BSC
      if (isConnectedToBSC(chainId)) {
        toast.info(t('alreadyOnBSC') || 'You are already connected to BSC Mainnet');
        return;
      }

      // Try to switch first, if that fails, add the network
      try {
        await switchToBSCNetwork(provider);
        toast.success(t('networkSwitched') || 'Successfully switched to BSC Mainnet');
      } catch (switchError) {
        if (switchError.code === 4902) {
          await addBSCNetwork(provider);
          toast.success(t('networkAdded') || 'BSC Mainnet added to your wallet');
        } else {
          throw switchError;
        }
      }
    } catch (error) {
      console.error('Error adding network:', error);
      toast.error(error.message || t('failedToAddNetwork') || 'Failed to add network');
    } finally {
      setIsAddingNetwork(false);
    }
  };

  const handleAddToken = async () => {
    if (!isConnected) {
      toast.error(t('pleaseConnectWallet') || 'Please connect your wallet first');
      return;
    }

    setIsAddingToken(true);
    try {
      const provider = getWalletProvider();
      if (!provider) {
        throw new Error(t('noWalletFound') || 'No wallet provider found');
      }

      // Check if on BSC network
      if (!isConnectedToBSC(chainId)) {
        toast.warning(t('switchToBSCFirst') || 'Please switch to BSC Mainnet first');
        return;
      }

      await addTokenToWallet(provider);
      toast.success(t('tokenAdded') || 'PHONE token added to your wallet');
    } catch (error) {
      console.error('Error adding token:', error);
      toast.error(error.message || t('failedToAddToken') || 'Failed to add token');
    } finally {
      setIsAddingToken(false);
    }
  };

  const isOnBSC = isConnectedToBSC(chainId);

  return (
    <div className="add-to-wallet-container">
      <h3 className="add-to-wallet-title">{t('addToWallet') || 'Add to Wallet'}</h3>
      <div className="add-to-wallet-buttons">
        <button
          className={`add-to-wallet-btn ${isOnBSC ? 'btn-success' : ''}`}
          onClick={handleAddNetwork}
          disabled={isAddingNetwork || !isConnected}
        >
          {isAddingNetwork ? (
            <>
              <span className="btn-loader"></span>
              {t('adding') || 'Adding...'}
            </>
          ) : isOnBSC ? (
            <>
              ✓ {t('onBSC') || 'On BSC Mainnet'}
            </>
          ) : (
            <>
              📱 {t('addBSCNetwork') || 'Add BSC Network'}
            </>
          )}
        </button>

        <button
          className="add-to-wallet-btn"
          onClick={handleAddToken}
          disabled={isAddingToken || !isConnected || !isOnBSC}
          title={!isOnBSC ? t('switchToBSCFirst') || 'Switch to BSC Mainnet first' : ''}
        >
          {isAddingToken ? (
            <>
              <span className="btn-loader"></span>
              {t('adding') || 'Adding...'}
            </>
          ) : (
            <>
              🪙 {t('addPHONEToken') || 'Add PHONE Token'}
            </>
          )}
        </button>
      </div>
      <p className="add-to-wallet-note">
        {t('addToWalletNote') || 'Connect your wallet and click the buttons above to add BSC Mainnet and PHONE token.'}
      </p>
      <a 
        href={PHONE_TOKEN_BSCSCAN} 
        target="_blank" 
        rel="noopener noreferrer"
        className="bscscan-link"
      >
        🔍 {t('viewOnBSCScan') || 'View PHONE Token on BSCScan'}
      </a>
    </div>
  );
};

export default AddToWallet;
